// ForwardMessageModal.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const getId = (value) => String(value?._id || value || "");

const getUserName = (user) => {
    if (!user) return "Пользователь";

    const name = [user.firstName, user.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

    return name || user.email || user.username || "Пользователь";
};

const getChatTitle = (chat, currentUserId) => {
    if (!chat) return "Чат";
    if (chat.chatType === "saved") return chat.title || "Сохранённое";
    if (chat.chatType === "scene_guest") return chat.title || "Чат по 3D-сцене";

    if (chat.entity?.type && chat.entity.type !== "none") {
        return chat.entity.title || chat.title || "Обсуждение";
    }

    if (chat.isGroup || chat.chatType === "group") {
        return chat.title || "Общий чат";
    }

    if (chat.user) return getUserName(chat.user);

    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    const other = participants.find((item) => getId(item) !== getId(currentUserId));

    return getUserName(other);
};

const getChatSubtitle = (chat, currentUserId) => {
    if (!chat) return "";
    if (chat.chatType === "saved") return "Сохранённые сообщения";
    if (chat.chatType === "scene_guest") return chat.guestAccess?.customerName || "Заказчик без регистрации";
    if (chat.entity?.type === "product") return chat.entity.subtitle || "Обсуждение товара";
    if (chat.entity?.type === "order") return chat.entity.subtitle || "Обсуждение заказа";

    if (chat.isGroup || chat.chatType === "group") {
        return `${chat.participants?.length || 0} участников`;
    }

    if (chat.user) return chat.user.email || "Личный чат";

    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    const other = participants.find((item) => getId(item) !== getId(currentUserId));

    return other?.email || "Личный чат";
};

const getLastMessageText = (chat) => (
    chat?.lastMessage?.text ||
    chat?.lastMessage?.entity?.title ||
    chat?.lastMessage?.fileName ||
    ""
);

const normalizeSearch = (value) => (
    String(value || "")
        .toLocaleLowerCase("ru-RU")
        .replace(/\s+/g, " ")
        .trim()
);

const matchesSearch = (chat, query, currentUserId) => {
    const clean = normalizeSearch(query);
    if (!clean) return true;

    const haystack = normalizeSearch([
        getChatTitle(chat, currentUserId),
        getChatSubtitle(chat, currentUserId),
        getLastMessageText(chat),
        chat?.title,
        chat?.entity?.title,
        chat?.entity?.subtitle,
        chat?.guestAccess?.customerName,
    ].filter(Boolean).join(" "));

    return haystack.includes(clean);
};

const isDirectChat = (chat) => chat?.chatType === "direct";

const ChatRow = ({
    chat,
    currentUserId,
    selected,
    disabled,
    onToggle,
}) => {
    const title = getChatTitle(chat, currentUserId);
    const subtitle = getChatSubtitle(chat, currentUserId);

    return (
        <button
            type="button"
            className={`forward-message-AO__chat ${selected ? "is-selected" : ""}`}
            disabled={disabled}
            onClick={() => onToggle?.(chat)}
        >
            <span className="forward-message-AO__avatar">
                {title.slice(0, 1).toUpperCase()}
            </span>

            <span className="forward-message-AO__chat-main">
                <b>{title}</b>
                {!!subtitle && <small>{subtitle}</small>}
            </span>

            <span className={`forward-message-AO__check ${selected ? "is-selected" : ""}`} aria-hidden="true">
                {selected ? "✓" : ""}
            </span>
        </button>
    );
};

export default function ForwardMessageModal({
    open = false,
    messages = [],
    chats = [],
    currentUserId = null,
    sourceChatId = null,
    query = "",
    onQueryChange = null,
    selectedChatIds = [],
    maxChats = 10,
    busy = false,
    progressText = "",
    error = "",
    onClose = null,
    onToggleChat = null,
    onSubmit = null,
}) {
    const searchRef = useRef(null);
    const selectedSet = useMemo(
        () => new Set((selectedChatIds || []).map(getId)),
        [selectedChatIds]
    );

    const filtered = useMemo(() => (
        (Array.isArray(chats) ? chats : [])
            .filter(Boolean)
            .filter((chat) => getId(chat._id) !== getId(sourceChatId))
            .filter((chat) => matchesSearch(chat, query, currentUserId))
    ), [chats, sourceChatId, query, currentUserId]);

    const directChats = useMemo(() => filtered.filter(isDirectChat), [filtered]);
    const otherChats = useMemo(() => filtered.filter((chat) => !isDirectChat(chat)), [filtered]);

    useEffect(() => {
        if (!open) return undefined;

        const frame = window.requestAnimationFrame(() => {
            searchRef.current?.focus?.({ preventScroll: true });
        });

        const handleKeyDown = (event) => {
            if (event.key === "Escape" && !busy) onClose?.();
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, busy, onClose]);

    if (!open || !messages.length || typeof document === "undefined") return null;

    const total = directChats.length + otherChats.length;
    const selectedCount = selectedSet.size;
    const messageCount = messages.length;

    const modal = (
        <div
            className="forward-message-AO"
            role="dialog"
            aria-modal="true"
            aria-label="Переслать сообщения"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) onClose?.();
            }}
        >
            <div
                className="forward-message-AO__panel"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="forward-message-AO__head">
                    <div>
                        <h3>Переслать...</h3>
                        <p>
                            {messageCount === 1 ? "1 сообщение" : `${messageCount} сообщений`}
                            {` · выберите до ${maxChats} чатов`}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="forward-message-AO__close"
                        onClick={() => !busy && onClose?.()}
                        disabled={busy}
                        title="Закрыть"
                        aria-label="Закрыть"
                    >
                        <CloseRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                </div>

                <label className="forward-message-AO__search">
                    <SearchRoundedIcon sx={{ fontSize: 20 }} />
                    <input
                        ref={searchRef}
                        type="search"
                        value={query}
                        onChange={(event) => onQueryChange?.(event.target.value)}
                        placeholder="Поиск по чатам"
                        aria-label="Поиск по чатам"
                        disabled={busy}
                    />
                </label>

                {!!error && (
                    <div className="forward-message-AO__error" role="alert">
                        {error}
                    </div>
                )}

                <div className="forward-message-AO__list">
                    {!total && (
                        <div className="forward-message-AO__empty">
                            <b>Ничего не найдено</b>
                            <span>
                                {query
                                    ? "Попробуйте изменить запрос."
                                    : "Нет других доступных чатов для пересылки."}
                            </span>
                        </div>
                    )}

                    {!!directChats.length && (
                        <section className="forward-message-AO__section">
                            <div className="forward-message-AO__section-title">
                                Личные
                                <span>{directChats.length}</span>
                            </div>

                            <div className="forward-message-AO__rows">
                                {directChats.map((chat) => (
                                    <ChatRow
                                        key={chat._id}
                                        chat={chat}
                                        currentUserId={currentUserId}
                                        selected={selectedSet.has(getId(chat._id))}
                                        disabled={busy}
                                        onToggle={onToggleChat}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {!!otherChats.length && (
                        <section className="forward-message-AO__section">
                            <div className="forward-message-AO__section-title">
                                Чаты
                                <span>{otherChats.length}</span>
                            </div>

                            <div className="forward-message-AO__rows">
                                {otherChats.map((chat) => (
                                    <ChatRow
                                        key={chat._id}
                                        chat={chat}
                                        currentUserId={currentUserId}
                                        selected={selectedSet.has(getId(chat._id))}
                                        disabled={busy}
                                        onToggle={onToggleChat}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <div className="forward-message-AO__footer">
                    <div className="forward-message-AO__selected-count">
                        Выбрано чатов: <b>{selectedCount}</b> / {maxChats}
                    </div>

                    <button
                        type="button"
                        className="forward-message-AO__submit"
                        disabled={busy || selectedCount === 0}
                        onClick={() => onSubmit?.()}
                    >
                        {busy ? (
                            <>
                                <i className="forward-message-AO__spinner" />
                                <span>{progressText || "Пересылаем..."}</span>
                            </>
                        ) : (
                            <span>
                                Переслать {messageCount > 1 ? `${messageCount} сообщ.` : "сообщение"}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
